import { serialize, type Selection } from '../urlState'

/**
 * A PANE IS THE WHOLE APP, POINTED AT ONE CONVERSATION — owner, 2026-08-22, after
 * asking for drag-to-split: 一次到位.
 *
 * WHY AN IFRAME AND NOT AN EXTRACTED COMPONENT. Everything a live conversation needs is
 * at App's top level and singular: one terminal socket, one composer, 65 top-level
 * states across 3948 lines. A second pane that can TYPE means instantiating all of it N
 * times — and the composer, the hold logic and the terminal input path are where months
 * of subtle bugs were fixed (predictive echo doing nothing on mobile because the
 * composer batch-submits; a selection hold freezing the queue; the uncontrolled
 * textarea that ended 輸入超卡). Rewriting that in one pass is how they come back.
 *
 * The app ALREADY supports "one conversation, addressed by a URL" — the owner is using
 * exactly that today, with two browser windows side by side. So a pane is that: the
 * real app, same origin, in a bare mode with the rail and the nav gone. Each pane gets
 * its own composer and its own terminal because each pane IS the thing that has them,
 * not a copy of it.
 *
 * THE COST, said out loud: N panes are N app instances, so the shell-level queries
 * (config, devices, projects) are asked N times. Two live conversations inherently cost
 * two transcript polls and two terminal sockets whichever way this is built; what the
 * iframe adds on top is a second React and a second set of shell queries. Against the
 * alternative — a rewrite of the input path on the night it ships — that is the cheaper
 * risk, and it is no worse than the two browser windows this replaces.
 */

/** True inside a pane iframe: render the stage alone, no rail, no nav. */
export const isPaneFrame = (): boolean => new URLSearchParams(location.search).has('pane')

/**
 * The URL for a pane showing `sel`.
 *
 * Built through `serialize`, never by hand: the selection encoding has rules that took
 * work — the stable hash+device for an agent, the opaque dmKey so a shared link never
 * leaks an email — and a second copy of them here would be a second place to forget.
 */
export function paneUrl(sel: Selection, view?: string | null): string {
  const q = serialize({ sel, view: view ?? null })
  const sep = q.startsWith('?') ? '&' : '?'
  return `${q}${sep}pane=1`
}
